/**
 * Quieting one conversation for a while.
 *
 * Muting a channel forever is a decision people make at eleven at night and
 * regret for a month; these are the spans somebody actually means - an hour,
 * until tomorrow, until they say otherwise - and the first two expire by
 * themselves.
 *
 * Stored on the account rather than the device, because a conversation you
 * quieted on the desktop should be quiet on the phone too.
 */

import { api } from './api';
import { useSession } from '@/stores/session';
import { queryClient } from '@/main';

export const MUTE_SPANS = [
  { minutes: 15, label: 'For 15 minutes' },
  { minutes: 60, label: 'For an hour' },
  { minutes: 8 * 60, label: 'For 8 hours' },
  { minutes: 24 * 60, label: 'Until tomorrow' },
  { minutes: 60 * 24 * 365 * 5, label: 'Until I turn it back on' }
] as const;

/** When this channel stops being quiet, or 0. */
export function mutedUntil(channelId: string | undefined): number {
  if (!channelId) return 0;
  const until = useSession.getState().settings?.mutes?.[channelId] ?? 0;
  return until > Date.now() ? until : 0;
}

export function isMuted(channelId: string | undefined): boolean {
  return mutedUntil(channelId) > 0;
}

/** "until 21:40", or "until tomorrow" - what to put under a Muted label. */
export function muteText(until: number): string {
  if (!until) return '';
  const left = until - Date.now();
  if (left > 1000 * 60 * 60 * 24 * 90) return 'until you turn it back on';
  const when = new Date(until);
  const sameDay = when.toDateString() === new Date().toDateString();
  return 'until ' + (sameDay ? '' : 'tomorrow ') + when.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

/** Mute it for a while, or pass 0 minutes to bring it back. */
export async function setMute(channelId: string, minutes: number): Promise<void> {
  const settings = useSession.getState().settings;
  const mutes = { ...(settings?.mutes ?? {}) };
  if (minutes) mutes[channelId] = Date.now() + minutes * 60 * 1000;
  else delete mutes[channelId];
  /* Shown as muted at once; the server is told straight after. */
  useSession.setState((s) => (s.settings ? { settings: { ...s.settings, mutes } } : s));
  await api.patch('/api/me/settings', { mute: { channelId, minutes } }).catch(() => {});
}

/**
 * Whether somebody is one of your pinned friends.
 *
 * Read off the friends list already in the cache rather than asked for: this
 * is called while deciding whether to make a noise, which is not a moment to
 * start a request.
 */
export function isFavorite(userId: string | undefined): boolean {
  if (!userId) return false;
  try {
    const data = queryClient.getQueryData<{ friends: { user: { id: string }; favorite?: boolean }[] }>(['friends']);
    return !!data?.friends?.some((f) => f.user.id === userId && f.favorite);
  } catch { return false; }
}
